import type {
  CheckoutEventError,
  CheckoutLifecycleEvent,
  CheckoutLifecyclePhase,
  CheckoutPaymentStatus,
  EventProduct,
  EventPromoCode,
  FrontendEvent,
  OrderAwardingEvent,
  PaymentIntentEvent,
  PaymentIntentStage,
} from '@appcharge/frontend-events';

import { ESdkEvents, EventParams } from '../contracts';
import { FrontendCheckoutEventType } from '../constants/frontendEvents';

type PromoCode = EventParams['promoCode'];

/**
 * Caller-supplied checkout event bodies — exact package DTO props + `event_type`.
 * Base `FrontendEvent` identity/device fields are stamped by the SDK enricher.
 */
export type CheckoutLifecycleEventInput = Omit<CheckoutLifecycleEvent, keyof FrontendEvent> & {
  event_type: typeof FrontendCheckoutEventType.CHECKOUT_LIFECYCLE;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type OrderAwardingEventInput = Omit<OrderAwardingEvent, keyof FrontendEvent> & {
  event_type: typeof FrontendCheckoutEventType.ORDER_AWARDING;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type PaymentIntentEventInput = Omit<PaymentIntentEvent, keyof FrontendEvent> & {
  event_type: typeof FrontendCheckoutEventType.PAYMENT_INTENT;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type CheckoutEventInput =
  | CheckoutLifecycleEventInput
  | OrderAwardingEventInput
  | PaymentIntentEventInput;

/** Checkout-specific fields shared by every package checkout DTO. */
type CheckoutEventCoreFields = {
  order_id: string;
  event_timestamp: string;
  bundle_id: string;
  bundle_name: string;
  bundle_sku: string;
  products: EventProduct[];
  session_id: string;
  total_sum: number;
  total_sum_currency: string;
  country_code: string;
  player_id: string;
  promo_code: EventPromoCode | null;
};

/**
 * Maps an `ESdkEvents` (postMessage) message to the matching
 * `@appcharge/frontend-events` DTO shape. Returns `null` for non-analytics
 * messages (theme / dimensions).
 */
export const toFrontendCheckoutEvent = (
  event: ESdkEvents,
  params: Partial<EventParams>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _publisherId?: string,
): CheckoutEventInput | null => {
  switch (event) {
    case ESdkEvents.CHECKOUT_OPENED:
    case ESdkEvents.ORDER_CREATED:
      return mapCheckoutLifecycleEvent('checkout_opened', params);
    case ESdkEvents.CLOSE_CHECKOUT:
      return mapCheckoutLifecycleEvent('checkout_closed', params);
    case ESdkEvents.ORDER_COMPLETED_SUCCESS:
      return mapOrderAwardingEvent('success', params);
    case ESdkEvents.ORDER_COMPLETED_FAILED:
      return mapOrderAwardingEvent('fail', params);
    case ESdkEvents.ON_PAYMENT_INTENT:
      return mapPaymentIntentEvent('started', 'pending', params);
    case ESdkEvents.PAYMENT_INTENT_SUCCESS:
      return mapPaymentIntentEvent('resolved', 'success', params);
    case ESdkEvents.PAYMENT_INTENT_FAILED:
      return mapPaymentIntentEvent('resolved', 'fail', params);
    default:
      return null;
  }
};

export const mapCheckoutLifecycleEvent = (
  phase: CheckoutLifecyclePhase,
  params: Partial<EventParams>,
): CheckoutLifecycleEventInput => {
  const dto: CheckoutLifecycleEventInput = {
    event_type: FrontendCheckoutEventType.CHECKOUT_LIFECYCLE,
    phase,
    ...mapCoreFields(params),
  };

  if (params.paymentMethodName) {
    dto.payment_method_name = params.paymentMethodName;
  }
  if (params.isSavedPaymentMethodUsed != null) {
    dto.is_saved_payment_method_used = params.isSavedPaymentMethodUsed;
  }
  if (params.isNewPaymentMethodSaved != null) {
    dto.is_new_payment_method_saved = params.isNewPaymentMethodSaved;
  }

  const error = mapLifecycleError(params);
  if (error) dto.error = error;

  return dto;
};

export const mapOrderAwardingEvent = (
  status: Extract<CheckoutPaymentStatus, 'success' | 'fail'>,
  params: Partial<EventParams>,
): OrderAwardingEventInput => ({
  event_type: FrontendCheckoutEventType.ORDER_AWARDING,
  ...mapCoreFields(params),
  ...mapPaymentFields(params),
  status,
  reason: status === 'fail' ? mapFailureReason(params) : null,
});

export const mapPaymentIntentEvent = (
  stage: PaymentIntentStage,
  status: CheckoutPaymentStatus,
  params: Partial<EventParams>,
): PaymentIntentEventInput => ({
  event_type: FrontendCheckoutEventType.PAYMENT_INTENT,
  ...mapCoreFields(params),
  ...mapPaymentFields(params),
  status,
  reason: status === 'fail' ? mapFailureReason(params) : null,
  stage,
});

export const mapCoreFields = (params: Partial<EventParams>): CheckoutEventCoreFields => ({
  order_id: params.orderId ?? '',
  event_timestamp: mapEventTimestamp(params.date),
  bundle_id: params.bundleId ?? '',
  bundle_name: params.bundleName ?? '',
  bundle_sku: params.bundleSKU ?? '',
  products: mapProducts(params),
  session_id: params.sessionId ?? '',
  total_sum: params.totalSum ?? 0,
  total_sum_currency: params.totalSumCurrency ?? '',
  country_code: params.userCountry ?? '',
  player_id: params.userId ?? '',
  promo_code: mapPromoCode(params.promoCode),
});

export const mapProducts = (params: Partial<EventParams>): EventProduct[] =>
  (params.products ?? []).map((product) => ({
    name: product.name,
    sku: product.sku,
    quantity: String(product.amount),
  }));

export const mapPaymentFields = (params: Partial<EventParams>) => ({
  payment_method_name: params.paymentMethodName ?? '',
  is_saved_payment_method_used: params.isSavedPaymentMethodUsed ?? false,
  is_new_payment_method_saved: params.isNewPaymentMethodSaved ?? false,
});

/** Maps FE camelCase promo → DTO snake_case `EventPromoCode`. */
export const mapPromoCode = (promoCode: PromoCode | null | undefined): EventPromoCode | null => {
  if (!promoCode || !promoCode.name) return null;
  return {
    name: promoCode.name,
    discount_amount: promoCode.discountAmount,
    discount_percentage: promoCode.discountPercentage,
  };
};

export const mapEventTimestamp = (date: string | undefined): string => {
  const epoch = Number(date);
  if (Number.isFinite(epoch) && epoch > 0) {
    return new Date(epoch).toISOString();
  }
  return new Date().toISOString();
};

export const mapFailureReason = (params: Partial<EventParams>): string | null =>
  params.reason ?? params.error?.reason ?? null;

export const mapLifecycleError = (params: Partial<EventParams>): CheckoutEventError | undefined => {
  if (!params.error && !params.reason) return undefined;
  const isFatal = params.error?.isFatal;
  const reason = params.error?.reason ?? params.reason;
  return {
    ...(isFatal != null ? { is_fatal: Boolean(isFatal) } : {}),
    ...(reason ? { reason } : {}),
  };
};
